import { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts';
import { LocationAnalysis } from '../../types/stats';

interface LocationPositionalAnalysisProps {
  data: LocationAnalysis[];
}

export const LocationPositionalAnalysis = ({ data }: LocationPositionalAnalysisProps) => {
  const barOption = useMemo(() => {
    if (!data || data.length === 0) {
      return null;
    }

    const locations = data.map((item) => item.location);
    const totalCounts = data.map((item) => item.totalCount);
    const rejected = data.map((item) => item.rejected);

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (params: any) => {
          const index = params[0].dataIndex;
          const location = data[index];
          return `
            <strong>${location.location}</strong><br/>
            Total: ${location.totalCount}<br/>
            Rejected: ${location.rejected}
          `;
        },
      },
      legend: {
        data: ['Total Count', 'Rejected'],
        top: 10,
      },
      grid: { left: 100, right: 30, top: 60, bottom: 60 },
      xAxis: {
        type: 'category',
        data: locations,
        axisLabel: { rotate: 45 },
      },
      yAxis: {
        type: 'value',
        name: 'Count',
        position: 'left',
      },
      series: [
        {
          name: 'Total Count',
          type: 'bar',
          data: totalCounts,
          itemStyle: { color: '#94a3b8' },
        },
        {
          name: 'Rejected',
          type: 'bar',
          data: rejected,
          itemStyle: { color: '#ef4444' },
        },
      ],
    } as echarts.EChartsOption;
  }, [data]);

  const oclockOption = useMemo(() => {
    if (!data || data.length === 0) {
      return null;
    }

    // Aggregate o'clock distribution across all locations
    const oclockMap = new Map<string, number>();
    data.forEach((location) => {
      Object.entries(location.oclockDistribution).forEach(([oclock, count]) => {
        oclockMap.set(oclock, (oclockMap.get(oclock) || 0) + count);
      });
    });

    if (oclockMap.size === 0) {
      return null;
    }

    // Helper function to extract position number and sort in clock order (12, 1, 2, ..., 11)
    const getPositionNumber = (pos: string): number => {
      const normalized = pos.toLowerCase().trim();
      const match = normalized.match(/(\d+)/);
      if (match) {
        const num = parseInt(match[1]);
        if (num >= 1 && num <= 12) {
          return num === 12 ? 12 : num;
        }
      }
      return 99; // Unknown positions go to end
    };

    const oclockData = Array.from(oclockMap.entries())
      .map(([oclock, count]) => ({ oclock, count }))
      .sort((a, b) => {
        const aNum = getPositionNumber(a.oclock);
        const bNum = getPositionNumber(b.oclock);
        
        // Handle 12 specially - it should come first
        if (aNum === 12 && bNum !== 12) return -1;
        if (bNum === 12 && aNum !== 12) return 1;
        if (aNum === 12 && bNum === 12) return 0;
        
        // For others, sort numerically
        return aNum - bNum;
      });

    // Extract labels and values in the sorted order
    const positions = oclockData.map((item) => item.oclock);
    const values = oclockData.map((item) => item.count);
    const maxValue = Math.max(...values, 1);

    // For radar chart, we need to reorder positions to display clockwise
    // ECharts places indicators counter-clockwise, so we reverse except for 12
    const reversedForClockwise = positions.length > 1 
      ? [positions[0], ...positions.slice(1).reverse()]
      : positions;

    // Reorder values to match the reordered positions
    // Create a map for quick lookup
    const valueMap = new Map<string, number>();
    positions.forEach((pos, idx) => {
      valueMap.set(pos, values[idx]);
    });
    
    const reorderedValues = reversedForClockwise.map((pos) => {
      return valueMap.get(pos) || 0;
    });

    return {
      tooltip: {
        show: false, // Disable tooltip
      },
      radar: {
        indicator: reversedForClockwise.map((position) => ({
          name: position,
          max: maxValue,
        })),
        center: ['50%', '50%'],
        radius: '70%',
        startAngle: 90, // Start from top (12 o'clock) - 90 degrees
        nameGap: 30, // Increased distance between label and axis to avoid overlap
        axisName: {
          color: '#333',
          fontSize: 12,
          fontWeight: 'bold',
        },
        splitArea: {
          show: true,
          areaStyle: {
            color: ['rgba(250, 250, 250, 0.3)', 'rgba(200, 200, 200, 0.3)'],
          },
        },
        axisLine: {
          lineStyle: {
            color: 'rgba(211, 253, 250, 0.8)',
          },
        },
        splitLine: {
          lineStyle: {
            color: 'rgba(211, 253, 250, 0.8)',
          },
        },
      },
      series: [
        {
          name: 'O\'Clock Distribution',
          type: 'radar',
          data: [
            {
              value: reorderedValues,
              name: 'Count',
              areaStyle: {
                color: 'rgba(59, 130, 246, 0.3)',
              },
              lineStyle: {
                color: '#3b82f6',
                width: 2,
              },
              itemStyle: {
                color: '#3b82f6',
              },
              label: {
                show: true,
                position: 'top', // Position labels inside the chart area to avoid overlap with axis labels
                formatter: (params: any) => {
                  // params.value is the value at this data point
                  // params.dataIndex is the index in the value array
                  if (params.value !== undefined && params.value !== null) {
                    return params.value.toString();
                  }
                  // Fallback: use dataIndex to get from reorderedValues
                  if (params.dataIndex !== undefined && params.dataIndex >= 0 && params.dataIndex < reorderedValues.length) {
                    return reorderedValues[params.dataIndex].toString();
                  }
                  return '';
                },
                fontSize: 11,
                fontWeight: 'bold',
                color: '#1e40af',
                offset: [0, -5], // Slight offset to position better
              },
            },
          ],
          emphasis: {
            areaStyle: {
              opacity: 0.5,
            },
          },
        },
      ],
    } as echarts.EChartsOption;
  }, [data]);

  if (!barOption) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-slate-500">
        No location data available.
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-slate-900">Location Analysis</h3>
          <p className="text-xs uppercase tracking-[0.3em] text-slate-400">Defect Rates by Location</p>
        </div>
        <ReactECharts 
          option={barOption} 
          style={{ height: 450 }} 
          key="location-analysis"
          notMerge={true}
          lazyUpdate={true}
        />
      </div>

      {oclockOption && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-slate-900">O'Clock Position Distribution</h3>
            <p className="text-xs uppercase tracking-[0.3em] text-slate-400">Radial View</p>
          </div>
          <ReactECharts 
            option={oclockOption} 
            style={{ height: 400 }} 
            key="oclock-distribution"
            notMerge={true}
            lazyUpdate={true}
          />
        </div>
      )}
    </div>
  );
};

